/**
 * Renders the deal background (content/briefing.ts) in two forms:
 *   - <BriefingSections> — the headed prose blocks, used inline on the title screen (Brief.tsx).
 *   - <BriefingPanel>    — the same content as a dismissible modal, opened by the in-game "Background"
 *                          toggle so a player can re-read the framing mid-deal without losing their place.
 *
 * Defined-term cast only (no invented names); see content/briefing.ts. Accessible: the panel is a
 * labelled modal dialog, closes on Escape, and focuses its close control on open.
 */
import { X } from "lucide-react";
import { DEAL_BACKGROUND } from "../content/briefing";
import { useFocusTrap } from "../lib/useFocusTrap";
import { cx } from "../lib/ui";

/** @param paneled wrap each section in a soft card (used on the title screen to lighten the wall of text). */
export function BriefingSections({ paneled = false }: { paneled?: boolean }) {
  return (
    <div className="flex flex-col gap-5">
      {DEAL_BACKGROUND.map((s) => (
        <section
          key={s.heading}
          className={cx("reading-measure", paneled && "rounded-sm border border-ink-150 bg-card p-5")}
        >
          <h2 className="t-overline mb-2 text-brand">{s.heading}</h2>
          <div className="flex flex-col gap-3 text-fluid-base leading-relaxed text-ink-700">
            {s.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
            {s.list && (
              <ul className="mt-1 flex list-none flex-col gap-2 border-l-2 border-ink-200 pl-4">
                {s.list.map((li, i) => (
                  <li key={i} className="relative">
                    <span className="mr-2 font-mono text-sm text-brand" aria-hidden="true">
                      {romanise(i + 1)}
                    </span>
                    {li}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}

/** The in-game modal showing the same background. */
export function BriefingPanel({ onClose }: { onClose: () => void }) {
  const ref = useFocusTrap<HTMLDivElement>(onClose);

  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="briefing-title"
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-4 outline-none backdrop-blur-sm sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[92svh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-ink-200 bg-card shadow-[var(--shadow-lg)]">
        <div className="flex items-center justify-between gap-3 border-b border-ink-150 px-5 py-3">
          <h2 id="briefing-title" className="t-overline text-ink-600">
            Deal background
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close background"
            className="rounded-sm p-1 text-ink-500 hover:text-ink"
          >
            <X size={18} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-5">
          <BriefingSections />
        </div>
      </div>
    </div>
  );
}

/** Lower-case roman numerals i–vi for the security-package list (matches the source's lettering). */
function romanise(n: number): string {
  return ["i", "ii", "iii", "iv", "v", "vi", "vii", "viii"][n - 1] ?? String(n);
}
